import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { classesApi, type ClassKind, type ClassStudent, type ClassSummary } from "./api";
import { useFeature } from "./features";
import { Lion } from "./Lion";
import { Avatar } from "./Sprite";
import "./Classes.css";
import "./guide.css";

// The teacher's Classes card on Home (decision 68): make a class, show its join code big on the
// projector, see who has joined, and take a student out of a class. A demo class (Review 14) is for
// the few outside guests on the owner's guest list; it's siloed from the school classes. Every
// teacher sees every class.

/** A small "Demo" label on anything from a demo class (teachers see both kinds). */
export function DemoBadge({ label = "Demo" }: { label?: string }) {
  return <span className="badge-demo">{label}</span>;
}

/** Where students go. The short address forwards to the Worker (decision 43). */
export const JOIN_URL = "buildlab.chahomework.com";

export function ClassesEntry() {
  const studentAccess = useFeature("studentAccess");
  const [classes, setClasses] = useState<ClassSummary[] | null>(null);
  const [name, setName] = useState("");
  const [kind, setKind] = useState<ClassKind>("class");
  const [error, setError] = useState<string | null>(null);
  const [showing, setShowing] = useState<ClassSummary | null>(null);
  const [rosterOf, setRosterOf] = useState<string | null>(null);

  const load = () => classesApi.list().then((r) => setClasses(r.classes), () => setClasses(null));
  useEffect(() => {
    void load();
  }, []);
  if (!classes) return null;

  return (
    <section aria-label="Classes" className="subs-entry classes-entry">
      <div className="classes-top">
        <div>
          <h2>Classes</h2>
          <p className="muted small">Students sign in with their school Google account, then type the class code once. A demo class is for guests on your guest list; it never mixes with school classes.</p>
          {!studentAccess && <p className="classes-warn small">Students can't sign in yet. Turn on “Students can sign in” under Features when the school says yes.</p>}
        </div>
        <form
          className="classes-new"
          onSubmit={async (e) => {
            e.preventDefault();
            setError(null);
            try {
              await classesApi.create(name, kind);
              setName("");
              setKind("class");
              await load();
            } catch (err) {
              setError((err as Error).message);
            }
          }}
        >
          <label htmlFor="new-class" className="sr-only">
            New class name
          </label>
          <input id="new-class" value={name} onChange={(e) => setName(e.target.value)} placeholder="8th Grade STEM (A)" maxLength={60} />
          <label htmlFor="new-class-kind" className="sr-only">
            Kind of class
          </label>
          <select id="new-class-kind" className="classes-kind" value={kind} onChange={(e) => setKind(e.target.value as ClassKind)}>
            <option value="class">School class</option>
            <option value="demo">Demo class (guests)</option>
          </select>
          <button type="submit" className="outline small" disabled={!name.trim()}>
            Create class
          </button>
        </form>
      </div>
      {error && <p className="dlg-error">{error}</p>}
      {classes.length === 0 ? (
        <p className="muted small">No classes yet. Create one, then show its code on the projector.</p>
      ) : (
        <ul className="class-list">
          {classes.map((k) => (
            <li key={k.id}>
              <div className="class-row">
                <span className="class-name">
                  {k.name}
                  {k.kind === "demo" && <DemoBadge />}
                  {k.createdBy && <span className="class-by muted small"> · by {k.createdBy}</span>}
                </span>
                <span className="class-count">
                  {k.students} {k.kind === "demo" ? "guest" : "student"}
                  {k.students === 1 ? "" : "s"}
                </span>
                <span className="class-code" aria-label={`Join code ${k.joinCode.split("").join(" ")}`}>
                  {k.joinCode}
                </span>
                <span className="class-actions">
                  <UnlockAllToggle
                    on={!!k.unlockAll}
                    label={`Unlock every tool for ${k.name}`}
                    onChange={async (on) => {
                      await classesApi.unlockAll(k.id, on);
                      await load();
                    }}
                  />
                  <button type="button" className="primary small" onClick={() => setShowing(k)}>
                    Show code
                  </button>
                  <button type="button" className="outline small" aria-expanded={rosterOf === k.id} onClick={() => setRosterOf(rosterOf === k.id ? null : k.id)}>
                    {rosterOf === k.id ? "Hide students" : "Students"}
                  </button>
                </span>
              </div>
              {rosterOf === k.id && <Roster classId={k.id} className={k.name} onChanged={load} />}
            </li>
          ))}
        </ul>
      )}
      {showing && <JoinCodeScreen cls={showing} onClose={() => setShowing(null)} />}
    </section>
  );
}

function Roster({ classId, className, onChanged }: { classId: string; className: string; onChanged: () => void }) {
  const [students, setStudents] = useState<ClassStudent[] | null>(null);
  const load = () => classesApi.students(classId).then((r) => setStudents(r.students), () => setStudents([]));
  useEffect(() => {
    void load();
  }, [classId]);
  if (!students) return <p className="muted small roster-empty">Loading…</p>;
  const removeClass = (
    <button
      type="button"
      className="linklike small roster-remove-class"
      onClick={async () => {
        if (!confirm(`Remove ${className}? Its code stops working, and its students will need a new class code. Their projects stay.`)) return;
        await classesApi.remove(classId);
        onChanged();
      }}
    >
      Remove this class
    </button>
  );
  if (students.length === 0)
    return (
      <div className="roster-empty">
        <p className="muted small">Nobody has joined yet. Show the code and have students type it in.</p>
        {removeClass}
      </div>
    );
  return (
    <div className="roster-wrap">
      <ul className="roster" aria-label={`Students in ${className}`}>
        {students.map((s) => (
          <li key={s.id}>
            <Avatar name={s.name || s.email} avatar={s.avatar} size={32} />
            <span className="roster-name">{s.name || s.email}</span>
            {s.guest && <span className="muted small">{s.email}</span>}
            <span className={`roster-rules small${s.rulesAccepted ? " ok" : ""}`}>{s.rulesAccepted ? "Rules accepted" : "Hasn't accepted the rules yet"}</span>
            <UnlockAllToggle
              on={!!s.unlockAll}
              label={`Unlock every tool for ${s.name || s.email}`}
              onChange={async (on) => {
                await classesApi.unlockStudent(classId, s.id, on);
                setStudents((all) => all && all.map((x) => (x.id === s.id ? { ...x, unlockAll: on } : x)));
              }}
            />
            {/* Parent emails (decision 85): only teachers add, change and see them. Guests are adults: none. */}
            {!s.guest && <ParentEmails classId={classId} student={s} onSaved={(parentEmails) => setStudents((all) => all && all.map((x) => (x.id === s.id ? { ...x, parentEmails } : x)))} />}
            <button
              type="button"
              className="linklike small"
              onClick={async () => {
                if (!confirm(`Take ${s.name || s.email} out of ${className}? Their projects stay. They'll need the class code to come back.`)) return;
                await classesApi.removeStudent(classId, s.id);
                await load();
                onChanged();
              }}
            >
              Remove
            </button>
          </li>
        ))}
      </ul>
      <ParentEmailsCopy students={students} />
      {removeClass}
    </div>
  );
}

/**
 * "Unlock everything" (Review 17, D46): tools unlock lesson by lesson; this gives a class, or one
 * student who's ready for more, every tool in every lesson. Students see it the next time they open a project.
 */
function UnlockAllToggle({ on, label, onChange }: { on: boolean; label: string; onChange: (on: boolean) => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  return (
    <label className="unlock-toggle small" title="Tools unlock lesson by lesson. This turns them all on.">
      <input
        type="checkbox"
        checked={on}
        disabled={busy}
        aria-label={label}
        onChange={async (e) => {
          setBusy(true);
          try {
            await onChange(e.target.checked);
          } finally {
            setBusy(false);
          }
        }}
      />
      Unlock everything
    </label>
  );
}

/** A student's parent/guardian emails on the roster, with Add/Edit for the teacher (up to two). */
function ParentEmails({ classId, student, onSaved }: { classId: string; student: ClassStudent; onSaved: (emails: string[]) => void }) {
  const emails = student.parentEmails ?? [];
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string[]>(["", ""]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const who = student.name || student.email;

  const save = async (list: string[]) => {
    setBusy(true);
    setError(null);
    try {
      const r = await classesApi.setParents(classId, student.id, list);
      onSaved(r.parentEmails);
      setEditing(false);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (!editing)
    return (
      <span className="roster-parents small">
        {emails.length ? (
          <>
            Parent:{" "}
            {emails.map((e, i) => (
              <span key={e}>
                {i > 0 && ", "}
                <a href={`mailto:${e}`}>{e}</a>
              </span>
            ))}
          </>
        ) : (
          <span className="muted">No parent email yet</span>
        )}{" "}
        <button
          type="button"
          className="linklike small roster-parent-edit"
          aria-label={`${emails.length ? "Edit" : "Add"} parent emails for ${who}`}
          onClick={() => {
            setDraft([emails[0] ?? "", emails[1] ?? ""]);
            setError(null);
            setEditing(true);
          }}
        >
          {emails.length ? "Edit" : "Add"}
        </button>
      </span>
    );

  return (
    <form
      className="roster-parents roster-parents-form small"
      noValidate
      aria-label={`Parent emails for ${who}`}
      onSubmit={(e) => {
        e.preventDefault();
        void save(draft.map((x) => x.trim()).filter(Boolean));
      }}
    >
      {draft.map((v, i) => (
        <input
          key={i}
          type="email"
          value={v}
          maxLength={254}
          autoComplete="off"
          autoFocus={i === 0}
          aria-label={i === 0 ? `Parent or guardian email for ${who}` : `Second parent or guardian email for ${who} (optional)`}
          placeholder={i === 0 ? "parent@example.com" : "Another parent (optional)"}
          onChange={(e) => setDraft((all) => all.map((x, j) => (j === i ? e.target.value : x)))}
        />
      ))}
      <button type="submit" className="primary small" disabled={busy}>
        Save
      </button>
      {emails.length > 0 && (
        <button type="button" className="outline small" disabled={busy} onClick={() => void save([])}>
          Remove both
        </button>
      )}
      <button type="button" className="linklike small roster-parent-edit" disabled={busy} onClick={() => setEditing(false)}>
        Cancel
      </button>
      {error && <p className="dlg-error">{error}</p>}
    </form>
  );
}

/** Every parent email in the class, comma-separated, ready to paste into an email's Bcc line. */
function ParentEmailsCopy({ students }: { students: ClassStudent[] }) {
  const [copied, setCopied] = useState(false);
  const all = [...new Set(students.flatMap((s) => s.parentEmails ?? []))];
  if (all.length === 0) return null;
  return (
    <p className="roster-parents-copy small">
      <button
        type="button"
        className="linklike small"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(all.join(", "));
            setCopied(true);
          } catch {
            window.prompt("Copy these parent emails:", all.join(", "));
          }
        }}
      >
        {copied ? "Copied!" : `Copy all parent emails (${all.length})`}
      </button>{" "}
      <span className="muted">Paste them into Bcc. BuildLab doesn't send email.</span>
    </p>
  );
}

/** The join code, full screen, for the projector. */
export function JoinCodeScreen({ cls, onClose }: { cls: { name: string; joinCode: string; kind?: ClassKind }; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  // On <body>, so the Home card's styles don't reach it.
  return createPortal(
    <div className="joincode-screen" role="dialog" aria-modal="true" aria-label={`Join code for ${cls.name}`}>
      <button type="button" className="outline small joincode-close" onClick={onClose} autoFocus>
        Close
      </button>
      <Lion pose="wave" size={130} />
      <p className="eyebrow gold">{cls.name}</p>
      <h2>Join our class</h2>
      <ol className="joincode-steps">
        <li>
          Go to <b>{JOIN_URL}</b>
        </li>
        <li>{cls.kind === "demo" ? "Sign in with the email your teacher added" : "Sign in with your school Google account"}</li>
        <li>Type this code:</li>
      </ol>
      <p className="joincode-code">{cls.joinCode}</p>
    </div>,
    document.body,
  );
}
